import { Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { WireService, WireComponentTabEnum } from '../services/wire.service';
import { BehaviorSubject, filter, of, Subscription } from 'rxjs';
import { WireModel } from '../models/wire.model';
import { BlockModel } from '../../../Common/PageBuilder/models/block.model';
import { ReleaseStatus } from '../../../api/wire/enums/release-status.enum';
import { AbstractPageBuilderService } from '../../../Common/PageBuilder/services/abstract-page-builder.service';
import { WirePageBuilderService } from '../services/wire-page-builder.service';
import { TabComponent } from '../../../Common/Layout/components/Tab/tab.component';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { UserModel } from '../../../Core/Users/models/user.model';
import { UsersService } from '../../../Core/Users/services/users.service';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { ButtonTypesEnum } from '../../../Common/Forms/enums/button-types.enum';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { WireConfirmationComponent } from './WireConfirmation/wire-confirmation.component';
import { AmplifyRoutingService } from '../../Core/services/amplify-routing.service';
import { AmplifyRouteModel } from '../../AmplifyRouter/models/amplify-route.model';
import { AmplifyRouteTypeEnum } from '../../AmplifyRouter/enums/amplify-route-type.enum';
import { WireHeaderEditorComponent } from './WireHeaderEditor/wire-header-editor.component';
import { WirePreviewDialogComponent } from './WirePreviewDialog/wire-preview-dialog.component';
import { validateEmail } from '../../../Common/Misc/helpers/formatting/email.helper';
import { ConfirmDialogComponent } from '../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { MessageService } from 'primeng/api';
import { WireActionTypeEnum } from '../../../api/wire/enums/wire-action-type.enum';
import { wireDefaultTabErrors, WireErrors } from '../enums/wire-errors';
import { WireValidationLevel } from '../enums/wire-validation-level.enum';
import { RightSidebarComponent } from '../../../Common/Layout/components/RightSidebar/right-sidebar.component';
import { ScrollPanelComponent } from '../../../Common/Basic/components/ScrollPanel/scroll-panel.component';
import { FormMessageComponent } from '../../../Common/Forms/components/FormMessage/form-message.component';
import { WireBlockTypeEnum } from '../enums/wire-block-type.enum';
import { WireContactEditorComponent } from './WireContactEditor/wire-contact-editor.component';
import { ImageUploadFieldModel } from '../../../Common/PageBuilder/models/image-upload-field.model';
import { ImageUploadFieldComponent } from '../../../Common/PageBuilder/components/FormFields/ImageUploadField/image-upload-field.component';
import { JournodexRegionsService } from '../../../Common/Journodex/services/journodex-regions.service';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { userPermissionsEnum } from '../../../Core/Users/enums/user-permissions.enum';
import { ImageHelper } from '../../../Common/PageBuilder/helpers/image.helper';
import { ImageDisplayElementModel } from '../../../Common/PageBuilder/models/image-display-element.model';
import { InputValidationModel } from '../../../Common/Forms/models/input-validation.model';
import { NotesChange } from '../../../Common/TargetingShared/components/UserNotes/user-notes.component';
import { AbstractAIHelperDialogService } from '../../../Common/Dialogs/services/abstract-AI-helper-dialog.service';
import { WireAIHelperDialogService } from '../services/wire-AI-helper-dialog.service';
import { ResourceActionTypes } from '../../../Common/Basic/enums/resource-actions.enum';
import { FocusHelper } from '../../../Common/Forms/helpers/focus.helper';
import { PageBuilderComponent } from '../../../Common/PageBuilder/components/PageBuilder/page-builder.component';
import { AgprComponentStyleEnum } from '../../../Common/Basic/enums/social-analysis-types.enum';
import { WireBannerErrorTypeEnum, bannerErrorsMap } from '../../../Amplify/Wire/enums/wire-banner-error-type.enum';
import { FullProfileDialogAnimationsEnum } from '../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../Core/Dialogs/enums/overlay-animation.enum';
import { FullProfileAnimationModel } from '../../../Core/Dialogs/models/full-profile-animation.model';
import { NewResourceDialogModel } from '../../../Common/Dialogs/models/new-resource-dialog.model';
import { NewResourceDialogComponent } from '../../../Common/Dialogs/components/NewResourceDialog/new-resource-dialog.component';
import { DisplayGroupModel } from '../../../Common/Layout/models/display-group.model';
import { allowedFileTypes, FileUploadModel } from '../../../Common/Forms/models/file-upload.model';
import {
    ManageContentLibraryDialogComponent
} from '../../../Common/ContentLibrary/components/ManageContentLibraryDialog/manage-content-library-dialog.component';
import { MediaSummaryModel } from '../../../Common/ContentLibrary/models/content-library-media-summary.model';

@Component({
    selector: 'agpr-wire',
    templateUrl: './wire.component.html',
    styleUrls: ['./wire.component.scss'],
    providers: [
        {
            provide: AbstractPageBuilderService,
            useExisting: WirePageBuilderService
        },
        {
            provide: AbstractAIHelperDialogService,
            useClass: WireAIHelperDialogService
        },
    ],
    standalone: false
})
export class WireComponent implements OnInit, OnDestroy {

    readonly chipValidations = [ new InputValidationModel({
        errorKey: 'Monitoring.keyWords.minimumKeywordCharacterLength',
        isValid: (input: string): boolean => {
            return input.trim().length > 1;
        }
    })];

    subscriptions: Subscription[];
    public selectedTabIndex: WireComponentTabEnum;
    public tabEnum = WireComponentTabEnum;
    public wire: WireModel;
    public selectedBlock: BlockModel;

    wireDirty = false;
    fileUploadLoading = false;
    hasEditorialPermission = false;
    showQuotaWarning: boolean = false;
    loading: boolean = false;
    usersMap: {[key: number]: string} = [];
    currentUser: UserModel;
    editable = false;
    notesDisabled: boolean = false;
    savePending: boolean = false;
    public bannerErrorList: string[] = [];
    public isShowBannerMessage: boolean = false;
    public isBannerExpanded: boolean = false;
    openAiPermission: boolean = false;

    public readonly defaultSelectedTabIndex: number = 0;
    public readonly blockFormTabIndex: number = 1;

    readonly messageSeverities = messageSeverityEnum;
    readonly buttonTypes = ButtonTypesEnum;
    readonly releaseStatuses = ReleaseStatus;
    readonly amplifyRouteTypes = AmplifyRouteTypeEnum;
    readonly wireBlockTypes = WireBlockTypeEnum;
    public readonly agprComponentStyleEnum = AgprComponentStyleEnum;

    protected currentRoute: AmplifyRouteModel;

    errors: WireErrors = new WireErrors();

    companyLogoField: ImageUploadFieldModel = new ImageUploadFieldModel(
        ImageUploadFieldComponent,
        'url',
        'Amplify.fakeBlockType.companyLogo.fieldLabel',
        'PageBuilder.formField.uploadDescription',
        5000000,
        'image/gif, image/jpeg, image/png, image/jpg',
        'PageBuilder.formField.uploadButtonLabel'
    );

    overlayContainer: Element;
    dateFormat: string;

    @ViewChild('centerScroll') centerScroll: ScrollPanelComponent;
    @ViewChild('centerScroll', {read: ElementRef}) centerScrollER: ElementRef;
    @ViewChild('rhp') rightSidebar: RightSidebarComponent;
    @ViewChild('blockFormTab') blockFormTab: TabComponent;
    @ViewChildren(FormMessageComponent, { read: ElementRef }) formErrors: QueryList<ElementRef>;
    @ViewChild(PageBuilderComponent) pageBuilder: PageBuilderComponent;
    animationTrigger: any;

    logoImage: FileUploadModel[]; // Single file, but FileUploadComponent tracks an array
    readonly imageMIMETypesRegex: string = allowedFileTypes.imageMIMETypesRegex;
    readonly imageMIMETypes: string = [
        ...allowedFileTypes.imageMIMETypesRegex.split('|').map(x => x[x.length - 1] === '$' ? x.substring(0, x.length - 1) : x),
    ].join(',');

    constructor(
        public wireService: WireService,
        // journodexRegionsService is used in html for country suggestions
        public journodexRegionsService: JournodexRegionsService,
        private wirePageBuilderService: WirePageBuilderService,
        private usersService: UsersService,
        private currentUserStateService: CurrentUserStateService,
        private coreDialogService: CoreDialogService,
        private amplifyRoutingService: AmplifyRoutingService,
        private messageService: MessageService,
        private localizationService: LocalizationService,
        public titleService: Title,
        public ref: ElementRef,
    ) {
        this.loadSubscriptions();
    }

    ngOnInit(): void {
        this.overlayContainer = document.getElementById('main-content');
    }

    private loadSubscriptions(): void {
        this.subscriptions = [];

        this.subscriptions.push(
            this.wireService.isWireEditable$.subscribe(isEditable => this.editable = isEditable),
            this.currentUserStateService.getPermissions().subscribe(
                permissions => {
                    if (permissions && permissions.indexOf(userPermissionsEnum.wireEditorial) !== -1) {
                        this.hasEditorialPermission = true;
                    }
                }
            ),
            this.wirePageBuilderService.getSelectedBlock().subscribe(
                (block: BlockModel) => {
                    this.selectedBlock = block;

                    if (this.selectedBlock) {
                        if (this.selectedBlock.type.key === WireBlockTypeEnum.contact) {
                            // based on mockup, use popup to fill contact data instead of RHP fields
                            this.coreDialogService.open(WireContactEditorComponent, {
                                outputs: {
                                    dialogClose: () => {
                                        this.wireService.checkErrorsForContactBlock(this.selectedBlock);
                                        this.wirePageBuilderService.deselectFakeBlock();
                                    }
                                }
                            });
                            if (this.blockFormTab) {
                                this.blockFormTab.disabled = true;
                            }
                            this.selectedTabIndex = this.defaultSelectedTabIndex;

                            return;
                        }

                        if (this.selectedBlock.type.key === WireBlockTypeEnum.companyLogo) {
                            this.companyLogoField.imageUploadState = !!this.wire?.companyLogo.url ?
                                'Amplify.wire.contactEditor.hasImage' : 'Amplify.wire.contactEditor.noImage';
                        }
                        // HACK: changing the tab index here doesn't actually select it unless the block form tab is already enabled
                        // This is because the disabled property in the template is handled after the tab index
                        // So we change the disabled state manually to handle that
                        if (this.blockFormTab) {
                            this.blockFormTab.disabled = false;
                        }
                        this.selectedTabIndex = this.blockFormTabIndex;

                        if (block.type.key !== WireBlockTypeEnum.text && block.type.key !== WireBlockTypeEnum.contact) {
                            let formSelector;
                            if (block.type.key !== WireBlockTypeEnum.companyLogo && block.type.key !== WireBlockTypeEnum.companyAddress) {
                                formSelector = 'agpr-block-form';
                            } else {
                                formSelector = '.agpr-wire-fakeBlock-blockForm';
                            }
                            requestAnimationFrame(() => {
                                const blockForm = document.querySelector(formSelector);
                                const firstInput: HTMLElement = blockForm.querySelector('input, button, [role=button], textarea');
                                firstInput.focus();
                            });
                        }
                    } else if (this.selectedTabIndex === this.blockFormTabIndex) {
                        this.selectedTabIndex = this.defaultSelectedTabIndex;
                    }
                }
            ),
            this.wireService.currentResource$.subscribe( wire => {
                this.wire = wire;
                this.errors = new WireErrors();
                this.setNotesDisabled();
                if (this.wire?.blocks) {
                    const imageBlocks = this.wire.blocks.filter(block => block.type.key === WireBlockTypeEnum.image);
                    if (imageBlocks.length > 0) {
                        this.checkForImageHeightErrors(imageBlocks);
                        this.calculateImageFileSize(imageBlocks);
                    }
                }
                if (wire) {
                    this.titleService.setTitle(
                        this.localizationService.transform('Amplify.newswire.browserTitle.name', {name: wire.name})
                    );
                }

                if (this.wire?.companyLogo?.url && (this.logoImage?.[0]?.url !== this.wire.companyLogo.url)) {
                    this.logoImage = [
                        {
                            id: this.wire.companyLogo.url,
                            name: this.localizationService.transform('Discover.fileUpload.existing.image'),
                            size: 0,
                            type: 'N/A'
                        }
                    ];
                }
            }),
            this.wirePageBuilderService.getFakeBlockDataChange().subscribe(x => this.clearFakeBlockError()),
            this.wireService.loading$.subscribe(x => {
                this.loading = x;
                if (!this.loading && this.blockFormTab && this.selectedTabIndex) {
                    this.blockFormTab.disabled = true;
                    this.selectedTabIndex = this.defaultSelectedTabIndex;
                }
                if (!this.loading && this.savePending) {
                    this.savePending = false;
                }
            }),
            this.wireService.resourceDirty$.subscribe(x => {
                this.wireDirty = x;
            }),
            this.currentUserStateService.getAccountUsers().subscribe(users => {
                this.usersMap = {};
                if (users) {
                    users.forEach(user => {
                        this.usersMap[user.id] = user.getDisplayName();
                    });
                }
            }),
            this.currentUserStateService.getUser().subscribe(x => this.currentUser = x),
            this.amplifyRoutingService.getCurrentRoute().subscribe(x => {
                this.currentRoute = x;
                this.setNotesDisabled();
            }),
            this.wireService.quotaWarning$.subscribe(x => this.showQuotaWarning = x),
            this.currentUserStateService.getPermissions().pipe(filter(x => !!x)).subscribe(permissions => {
                if (permissions?.length) {
                    this.openAiPermission = permissions.includes(userPermissionsEnum.openAI);
                }
            }),
            this.wireService.isWireNameDuplicated().pipe(filter(x => !!x)).subscribe(x => {
                document.querySelector<HTMLElement>('#wires_wireNameTextInput')?.focus();
            }),
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(prefs => this.dateFormat = prefs.getDateFormatString())
        );
    }

    @HostListener('window:beforeunload', ['$event'])
    pageUnload($event) {
        if (this.wireDirty) {
            $event.returnValue = this.localizationService.transform('DeactivateGuard.messageText');
        }
    }

    checkForImageHeightErrors(blocks: BlockModel[]) {
        blocks.forEach(block => {
            block.errors = [];
            const image = block.displayElement as ImageDisplayElementModel;
            if (image.image?.url) {
                ImageHelper.getImageHeight(image.image?.url).then(height => {
                    if (height < 500) {
                        if (block.errors.length === 0) {
                            block.errors.push('Amplify.wire.imageHeightWarning');
                        }
                    }
                }).catch(err => { });
            }
        });
    }

    calculateImageFileSize(blocks: BlockModel[]): void {
        blocks.forEach(block => {
            block.errors = [];
            const image = block.displayElement as ImageDisplayElementModel;
            if (image.image?.url) {
                ImageHelper.getImageFileSize(image.image.url)
                    .then(size => block.imageFileSize = size)
                    .catch(error => {
                        block.imageFileSize = null;
                        if (block.errors.length === 0) {
                            block.errors.push('Amplify.wire.error.block.invalidImage');
                        }
                    });
            }
        });
    }

    ngOnDestroy(): void {
        for (const subscription of this.subscriptions) {
            subscription.unsubscribe();
        }
    }

    public sidebarTabChanged(event): void {
    }

    deliveryScheduleChanged(event) {
        this.errors.hasScheduleDateError = !this.wire.deliverySchedule.isValid();
        this.wireService.currentResourceModified();
    }

    setNewReleaseNameDialog(releaseAction: WireActionTypeEnum, releaseValidationLevel: WireValidationLevel) {
        const textInputDialogConfig: NewResourceDialogModel = {
            inputs: {
                heading: 'Amplify.wire.dialogs.wireName.save',
                description: 'Amplify.wire.dialogs.wireName.description',
                placeholder: 'Amplify.wire.dialogs.wireName.placeholder',
                okLabel: 'Amplify.wire.dialogs.wireName.save',
                cancelLabel: 'Core.button.cancel',
                width: 300,
                submit: (name: string, group?: DisplayGroupModel) => {
                    this.wire.groupId = group?.id;
                    if (releaseAction === WireActionTypeEnum.save) {
                        this.saveWire();
                    } else {
                        this.submit();
                    }
                    return of(null);
                },
                formValidator: (wireName: string,) => {
                    this.wire.name = wireName.trim();
                    const { nameExists } = this.wireService.getValidationErrors(this.wire, releaseValidationLevel);
                    return nameExists ? ['Amplify.wire.error.duplicateName'] : [];
                }
            }
        };
        this.coreDialogService.open(NewResourceDialogComponent, textInputDialogConfig);
    }

    saveWire() {
        this.wire.name = this.wire.name?.trim();
        if (!this.validateWire(WireValidationLevel.DRAFT)) {
            if (!this.wire.name || !(this.wire.name.trim().length > 0)) {
                this.setNewReleaseNameDialog(WireActionTypeEnum.save, WireValidationLevel.DRAFT);
                return;
            }

            if (this.wire.id) {
                this.wireService.updateWire(this.wire, {actionType: ResourceActionTypes.SAVE, refreshResource: true}).subscribe(() => {
                    // AD-15472 - REMOVE AFTER WE FIX WEBSOCKETS
                    this.wireService.refreshWireList();
                });
            } else {
                this.wireService.createResource(this.wire, {actionType: ResourceActionTypes.SAVE, refreshResource: true}).subscribe(id => {
                    this.amplifyRoutingService.gotoWire(id);

                    // AD-15472 - REMOVE AFTER WE FIX WEBSOCKETS
                    this.wireService.refreshWireList();
                });
            }
        }
        this.isShowBannerMessage = false;
    }

    saveNotes(notesChange: NotesChange) {
        this.wire.userNotes = notesChange?.notes;
        this.wireService.saveNotes(notesChange, this.wire).subscribe();
    }

    submit() {
        if (!this.validateWire(WireValidationLevel.SUBMITTED)) {
            if (!this.wire.name || !(this.wire.name.trim().length > 0)) {
                this.setNewReleaseNameDialog(WireActionTypeEnum.submit, WireValidationLevel.SUBMITTED);
                return;
            }
            this.coreDialogService.open(WireConfirmationComponent, {
                inputs: {
                    additionalDisclaimer: 'Amplify.wire.confirmation.submit.disclaimer2',
                }
            });
        } else {
            this.focusErrorBanner();
        }
    }

    cancel(): void {
        this.wire.status = ReleaseStatus.draft;
        this.wireService.updateWire(this.wire, {
            actionType: ResourceActionTypes.CANCEL,
            refreshResource: true,
        }).subscribe();
    }

    previewWire(): void {
        this.coreDialogService.open(WirePreviewDialogComponent);
    }

    publish() {
        if (!this.validateWire(WireValidationLevel.SUBMITTED)) {
            this.coreDialogService.open(WireConfirmationComponent, {
                inputs: {
                    editorialMode: true,
                    heading: 'Amplify.wire.confirmation.publish.heading',
                    disclaimer: 'Amplify.wire.confirmation.publish.disclaimer'
                }
            });
        } else {
            this.focusErrorBanner();
        }
    }

    retract() {
        this.coreDialogService.open(ConfirmDialogComponent, {
            inputs: {
                id: 'wire.retractConfirmDialog',
                heading: 'Amplify.wire.actions.retractPopup.heading',
                message: 'Amplify.wire.actions.retractPopup.message',
                okLabel: 'Amplify.wire.actions.retractPopup.okLabel',
            },
            outputs: {
                dialogClose: (response) => {
                    if (response) {
                        this.wire.status = ReleaseStatus.retracted;
                        this.wireService.updateWire(this.wire, {actionType: ResourceActionTypes.RETRACT, refreshResource: true})
                            .subscribe(() => {
                                this.wireService.refreshWireList();
                            });
                    }
                }
            }
        });
    }

    focusErrorBanner() {
        requestAnimationFrame(() => document.querySelector<HTMLElement>('div.agpr-wire-bannerMessage .agpr-message-toggleIcon')?.focus());
    }

    setBannerErrorList(): void {
        const errorArray = [];
        if (this.errors.hasNoHeadline || this.errors.hasNoCompanyName || this.errors.hasNoDateline || this.errors.headlineExceedsByteLengthLimit) {
            errorArray.push(bannerErrorsMap.get(WireBannerErrorTypeEnum.WIRE_HEADER_ERROR));
        }
        if (this.errors.hasCompanyLogoError) {
            errorArray.push(bannerErrorsMap.get(WireBannerErrorTypeEnum.WIRE_LOGO_ERROR));
        }
        if (this.pageBuilder?.getErrorElements()?.length) {
            const result = this.wirePageBuilderService.getBlockErrors(this.wire.blocks);
            for (let i = 0; i < result.length; i++) {
                errorArray.push(
                    {
                        errorId: result[i].id,
                        label: result[i].label
                    }
                );
            }
        }
        if (this.errors.hasCompanyAddressError) {
            errorArray.push(bannerErrorsMap.get(WireBannerErrorTypeEnum.WIRE_ADDRESS_ERROR));
        }
        if (this.errors.hasNoContactName) {
            errorArray.push(bannerErrorsMap.get(WireBannerErrorTypeEnum.WIRE_CONTACT_NAME_ERROR));
        }
        if (this.errors.hasNoContactEmail || this.errors.wrongEmailFormat) {
            errorArray.push(bannerErrorsMap.get(WireBannerErrorTypeEnum.WIRE_CONTACT_EMAIL_ERROR));
        }
        this.bannerErrorList = errorArray;
        this.isShowBannerMessage = this.bannerErrorList.length > 0;
    }

    isMessageExpanded(event: boolean) {
        this.isBannerExpanded = !event;
    }

    focusErrorElement(errorId: string) {
        requestAnimationFrame(() => {
            if (wireDefaultTabErrors.filter(error => this.errors[error]).length > 0) {
                this.selectedTabIndex = this.defaultSelectedTabIndex;
            }
            FocusHelper.focusError(errorId);
        });
    }

    openHeaderEditor() {
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        this.coreDialogService.open(WireHeaderEditorComponent, {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: this.overlayContainer,
                openAiPermission: this.openAiPermission,
                title: 'Amplify.wire.headerEditor.header'
            },
            outputs: {
                dialogClose: () => {
                    const {
                        hasNoHeadline,
                        hasNoCompanyName,
                        hasNoDateline
                    } = this.wireService.getValidationErrors(this.wire, WireValidationLevel.HEADER);
                    this.errors.hasNoHeadline = hasNoHeadline;
                    this.errors.hasNoCompanyName = hasNoCompanyName;
                    this.errors.hasNoDateline = hasNoDateline;

                }
            }
        });
    }

    validateWire(wireValidationLevel: WireValidationLevel): boolean {
        this.errors = this.wireService.getValidationErrors(this.wire, wireValidationLevel);
        this.setBannerErrorList();
        if (wireValidationLevel === WireValidationLevel.SUBMITTED) {
            if (this.errors.hasNoTextBlockError) {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: this.localizationService.transform('Amplify.wire.error.growlMessage.noTextBlock.summary'),
                    detail: this.localizationService.transform('Amplify.wire.error.growlMessage.noTextBlock.detail')
                });
            }

            if (this.showQuotaWarning) {
                this.errors.insufficientQuota = true;
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: this.localizationService.transform('Amplify.wire.error.growlMessage.insufficientQuota.summary'),
                    detail: this.localizationService.transform('Amplify.wire.error.growlMessage.insufficientQuota.detail')
                });
            }
            if (!Object.values(this.errors).some(Boolean)) {
                // in order to calculate total image file size, we need to fetch each image blob asynchronously
                // imageFileSize = false means it is not completed
                // if so, we give user a warning and let user to try later
                let finishFetchImageFileSize = true;
                this.wire.blocks.forEach(block => {
                    if (block.type.key === WireBlockTypeEnum.image && !block.imageFileSize) {
                        finishFetchImageFileSize = false;
                    }
                });
                if (!finishFetchImageFileSize) {
                    this.messageService.add({
                        severity: messageSeverityEnum.warning,
                        summary: this.localizationService.transform('Amplify.wire.warning.growlMessage.imageProcess.summary'),
                        detail: this.localizationService.transform('Amplify.wire.warning.growlMessage.imageProcess.detail')
                    });

                    return true;
                }
            }

            if (this.errors.hasImageTotalSizeError) {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: this.localizationService.transform('Amplify.wire.error.growlMessage.hasImageTotalSizeError.summary'),
                    detail: this.localizationService.transform('Amplify.wire.error.growlMessage.hasImageTotalSizeError.detail')
                });
            }
        }

        return Object.values(this.errors).some(Boolean);
    }

    validateContactEmail(): void {
        this.errors.wrongEmailFormat = !validateEmail(this.wire.contactEmail);
        this.errors.hasNoContactEmail = !this.wire?.contactEmail?.trim();
        this.wireService.currentResourceModified();
    }

    validateContactName(): void {
        this.errors.hasNoContactName = !this.wire?.contactName?.trim();
        this.wireService.currentResourceModified();
    }

    backToDraft(): void {
        this.coreDialogService.open(ConfirmDialogComponent, {
            inputs: {
                id: 'wire.backToDraftConfirmDialog',
                heading: 'Amplify.wire.actions.backToDraft',
                alertMessage: 'Amplify.wire.actions.backToDraft.warning',
                alertType: messageSeverityEnum.warning,
                message: 'Amplify.wire.actions.backToDraft.message',
                okLabel: 'Amplify.wire.actions.backToDraft.continue',
            },
            outputs: {
                dialogClose: async (response) => {
                    if (response) {
                        this.wire.status = ReleaseStatus.draft;
                        this.wireService.editorUpdate(this.wire, {actionType: ResourceActionTypes.BACK_TO_DRAFT, resetResource: true})
                            .subscribe(() => {
                                this.amplifyRoutingService.gotoEditorialHype();

                                // AD-15472 - REMOVE AFTER WE FIX WEBSOCKETS
                                this.wireService.refreshWireList();
                            });
                    }
                }
            }
        });
    }

    updateFakeBlocks(type: WireBlockTypeEnum): void {
        this.wirePageBuilderService.fakeBlockDataChange(type);
        this.wireService.currentResourceModified();
    }

    clearFakeBlockError(): void {
        if (!this.wire) {
            return;
        }

        if (!this.wire.companyLogo || this.wire.companyLogo.url) {
            this.errors.hasCompanyLogoError = false;
        }
        if (!this.wire.companyAddress?.isFieldEmpty()) {
            this.errors.hasCompanyAddressError = false;
        }
    }

    updateCompanyLogo(url: string): void {
        this.wire.companyLogo.url = url;
        this.companyLogoField.imageUploadState = !!this.wire.companyLogo.url ? 'Amplify.wire.contactEditor.hasImage' : 'Amplify.wire.contactEditor.noImage';
        this.updateFakeBlocks(WireBlockTypeEnum.companyLogo);
    }

    setNotesDisabled(): void {
        if (!this.currentRoute || !this.wire) {
            return;
        }

        this.notesDisabled = !(
            (this.currentRoute.type === AmplifyRouteTypeEnum.wire &&
                (this.wire.status === ReleaseStatus.draft || this.wire.status === ReleaseStatus.submitted)) ||
            (this.currentRoute.type === AmplifyRouteTypeEnum.editorial &&
                (this.wire.status === ReleaseStatus.submitted || this.wire.status === ReleaseStatus.scheduled))
        );
    }


    openContentLibrary(): void {
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        const io = {
            inputs: {
                animationTrigger,
                overlayContainer: this.overlayContainer,
                useAcceptedFilesTypes: true,
                selectionMode: true,
                singleSelect: true,
                navBarLaunch: false
            },
            outputs: {
                dialogClose: (selectedFiles: MediaSummaryModel[]) => {
                    if (selectedFiles && selectedFiles.length > 0) {
                        const fileUpload: FileUploadModel = {
                            id: selectedFiles[0].id,
                            name: selectedFiles[0].name,
                            size: selectedFiles[0].size,
                            url: selectedFiles[0].url
                        };
                        this.logoImage = [fileUpload];


                        this.wire.companyLogo.url = selectedFiles[0].url;
                        this.companyLogoField.imageUploadState = !!this.wire.companyLogo.url ?
                            'Amplify.wire.contactEditor.hasImage' : 'Amplify.wire.contactEditor.noImage';
                        this.updateFakeBlocks(WireBlockTypeEnum.companyLogo);
                    }
                }
            }
        };
        this.coreDialogService.open(ManageContentLibraryDialogComponent, io);
    }

    removeLogoImage() {
        this.wire.companyLogo.url = null;
        this.companyLogoField.imageUploadState = !!this.wire.companyLogo.url ?
            'Amplify.wire.contactEditor.hasImage' : 'Amplify.wire.contactEditor.noImage';
        this.updateFakeBlocks(WireBlockTypeEnum.companyLogo);
    }

}
